import { Injectable } from '@angular/core';
import { HttpHeaders, HttpClient, HttpResponse } from '@angular/common/http';
import * as _ from 'lodash';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/operator/retry';
import 'rxjs/add/operator/timeout';
import { ConfigService } from '../../core/config/config.service';
import { StatusCode } from './status-codes.enum';
import { Utility } from '../../core/helpers/utility';
import { ModalService } from '../../core/modal/modal.service';
import { AuthenticationService } from '../../core/security/services/authentication.service';
import { clearState } from '../../app-states/store/local-storage';

@Injectable()
export class ApiService {
  apiUrl = '';
  timeout = 1800000; // 30 min
  httpCallRetry = 0;
  options: any = {
    headers: this.headers,
    observe: 'response'
  };

  constructor(private authService: AuthenticationService,
    private config: ConfigService,
    private modal: ModalService,
    private http: HttpClient) {
    this.apiUrl = Utility.sanitizePath(config.appConfig.api.mcApiUrl);
    this.timeout = this.config.appConfig.api.httpCallTimeout;
    this.httpCallRetry = this.config.appConfig.api.httpCallRetry;
  }

  get(path: string): Observable<any> {
    return this.http.get(`${this.apiUrl}${path}`, this.options)
      .retry(this.httpCallRetry)
      .map(this.checkForError)
      .timeout(this.timeout)
      .map(this.extractData)
      .catch(this.handleError.bind(this));

  }

  post(path: string, body, responseType?: string): Observable<any> {
    var opts = { ...this.options };
    if (responseType) {
      opts.responseType = responseType;
    }

    return this.http.post(`${this.apiUrl}${path}`, JSON.stringify(body), opts)
      .retry(this.httpCallRetry)
      .map(this.checkForError)
      .timeout(this.timeout)
      .map(this.extractData)
      .catch(this.handleError.bind(this));
  }

  postVoid(path: string): Observable<any> {
    return this.http.post(`${this.apiUrl}${path}`, this.options)
      .retry(this.httpCallRetry)
      .map(this.checkForError)
      .timeout(this.timeout)
      .map(this.extractData)
      .catch(this.handleError.bind(this));
  }

  delete(path: string): Observable<any> {
    return this.http.delete(`${this.apiUrl}${path}`, this.options)
      .retry(this.httpCallRetry)
      .map(this.checkForError)
      .timeout(this.timeout)
      .map(this.extractData)
      .catch(this.handleError.bind(this));
  }

  private handleError(error: any) {
    if (this.IsServerError(error)) {
      this.ShowErrorModal(error);
    }

    if (this.IsUnAuthorizedError(error)) {
      this.logout();
    }

    if (this.IsForbiddenError(error)) {
      this.modal.activateError(`Authorization  Error`, `An authorization error has occurred, 
      your token might have been expired, Please try again.
      If the problem persists please email support at missioncontrol@bio-rad.com `);
    }

    return Observable.throw({ error });
  }

  logout() {
    this.authService.logOut().subscribe((authenticated) => {
      clearState();
      location.reload();
    });
  }

  private IsServerError(error: any) {
    return (!error.status || error.status === StatusCode.ServerError || error.status === StatusCode.NoStatus);
  }

  private IsUnAuthorizedError(error: any) {
    return (error.status && error.status === StatusCode.UnAuthorized);
  }

  private IsForbiddenError(error: any) {
    return (error.status && error.status === StatusCode.Forbidden);
  }

  private ShowErrorModal(error: any) {
    const errMsg = `${error.status || ''} - ${error.statusText || 'Error in communicating with server.'}`;
    this.modal.activateError(`Server Error`, `A system error has occurred. Please try again. 
    If the problem persists please email support at missioncontrol@bio-rad.com` + errMsg);
  }

  private checkForError(response: HttpResponse<any> | any) {
    if (response.status >= StatusCode.OK && response.status < StatusCode.Ambiguous) {
      return response;
    } else if (response.status === StatusCode.BadRequest) {
      return response;
    } else if (response.status === StatusCode.NotFound) {
      return response;
    } else {
      const error = new Error(response.statusText);
      error['response'] = response;
      console.error(error);
      throw error;
    }
  }

  private extractData(response: any) {
    if (response.body) {
      return response.body || {};
    } else {
      return response.body;
    }
  }

  private get headers() {
    const headers: HttpHeaders = new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('Accept', 'application/json')
      .set('Access-Control-Allow-Origin', '*')
      .set('Access-Control-Allow-Methods', 'GET, HEAD, OPTIONS, POST, PUT, DELETE')
      .set('Access-Control-Allow-Headers', 'Origin, X-Requested-With, Content-Type, Accept, Authorization');
    return headers;
  }

}



// WEBPACK FOOTER //
// /home/runner/work/cdg-missioncontrol/cdg-missioncontrol/MissionControlClient2/MissionControl.WebUi2/mc-app/src/app/shared/api/api.service.ts